// Upload Invoice: a PDF that didn't arrive by email. The server files it in
// Dropbox (the card's month folder; the sandbox folder when not in production)
// and logs it as a manual upload.

// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Alert, Button, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";

// API, CONSTANTS
import { postFormDataAPICall } from "../../../config/apiCalls";
import { apiError } from "../itBudgetUtils";

const EMPTY = { it_vendor_id: "", invoice_date: "", invoice_number: "", total: "", tax: "", it_payment_method_id: "", notes: "" };

const UploadInvoiceDialog = ({ open, lookups, onClose, onSaved }) => {
	const { instance, accounts } = useMsal();
	const [form, setForm] = useState(EMPTY);
	const [file, setFile] = useState(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) {
			setForm(EMPTY);
			setFile(null);
			setError("");
		}
	}, [open]);

	const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

	const save = async () => {
		setSaving(true);
		setError("");
		try {
			const data = new FormData();
			Object.entries(form).forEach(([k, v]) => v !== "" && data.append(k, v));
			if (file) data.append("file", file);
			await postFormDataAPICall(instance, accounts[0], "/api/it-budget/invoices", data);
			onSaved();
		} catch (err) {
			setError(apiError(err, "Upload failed."));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
			<DialogTitle className="itBudgetDialogTitle">Upload Invoice</DialogTitle>
			<DialogContent className="itBudgetDialogContent">
				<Collapse in={Boolean(error)}>
					<Alert severity="error">{error}</Alert>
				</Collapse>
				<Button variant="outlined" component="label">
					{file ? file.name : "Choose PDF"}
					<input type="file" accept="application/pdf,.pdf" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
				</Button>
				<TextField select label="Vendor" required value={form.it_vendor_id} onChange={set("it_vendor_id")}>
					{lookups.vendors.map((v) => (
						<MenuItem key={v.id} value={v.id}>
							{v.name}
						</MenuItem>
					))}
				</TextField>
				<div className="itBudgetFormGrid">
					<TextField label="Invoice Date" type="date" required InputLabelProps={{ shrink: true }} value={form.invoice_date} onChange={set("invoice_date")} />
					<TextField label="Invoice #" value={form.invoice_number} onChange={set("invoice_number")} />
					<TextField label="Total" required value={form.total} onChange={set("total")} placeholder="0.00" />
					<TextField label="Tax" value={form.tax} onChange={set("tax")} placeholder="0.00" />
				</div>
				<TextField select label="Paid By" value={form.it_payment_method_id} onChange={set("it_payment_method_id")} helperText="Leave empty to use the vendor's method">
					{/* The default, not a payment method: kept first and set apart from the alphabetical list. */}
					<MenuItem value="" divider sx={{ fontStyle: "italic", color: "text.secondary" }}>
						Use vendor's method (default)
					</MenuItem>
					{[...lookups.paymentMethods].sort((a, b) => a.display_name.localeCompare(b.display_name)).map((p) => (
						<MenuItem key={p.id} value={p.id}>
							{p.display_name}
						</MenuItem>
					))}
				</TextField>
				<TextField label="Notes" multiline minRows={2} value={form.notes} onChange={set("notes")} />
				<div className="itBudgetDialogButtons">
					<Button variant="outlined" color="error" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button variant="contained" onClick={save} disabled={saving || !file} startIcon={saving ? <CircularProgress size={16} /> : null}>
						Upload
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default UploadInvoiceDialog;
